import {
  Box,
  Divider,
  Heading,
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Text,
  useColorModeValue,
  VStack,
} from "@chakra-ui/react";
import { PatientDTO } from "@metriport/api-sdk";
import { toArray } from "@metriport/shared";
import { useMemo } from "react";
import { PatientFacilityMatches } from "../../../api/patient-matches";

export type isDownloading = Record<
  string,
  {
    type?: string;
    downloading: boolean;
  }
>;

type ContactInfo = {
  phones: string[];
  emails: string[];
  addresses: string[];
};

function extractContactInfoFromPatient(patient: PatientDTO | undefined): ContactInfo {
  if (!patient) {
    return { phones: [], emails: [], addresses: [] };
  }

  const contacts = toArray(patient.contact);
  const phones = contacts.flatMap(c => (c?.phone ? [c.phone] : []));
  const emails = contacts.flatMap(c => (c?.email ? [c.email] : []));

  const addressList = toArray(patient.address);
  const addresses = addressList.flatMap(addr => {
    if (!addr) return [];
    const line2 = addr.addressLine2 ? `${addr.addressLine2}, ` : "";
    return [`${line2}${addr.addressLine1}, ${addr.city}, ${addr.state}, ${addr.zip}`];
  });

  return { phones, emails, addresses };
}

function extractContactInfoFromMatches(matches: PatientFacilityMatches): ContactInfo {
  const allPhones: string[] = [];
  const allEmails: string[] = [];
  const allAddresses: string[] = [];

  for (const match of matches) {
    if (match.patient) {
      allPhones.push(...match.patient.telephoneNumbers);
      allEmails.push(...match.patient.emails);
      allAddresses.push(...match.patient.addresses);
    }
  }

  return {
    phones: allPhones.filter(Boolean),
    emails: allEmails.filter(Boolean),
    addresses: allAddresses.filter(Boolean),
  };
}

function dedupe(items: string[], normalize: (s: string) => string): string[] {
  const seen = new Set<string>();
  return items.filter(item => {
    const normalized = normalize(item);
    if (seen.has(normalized)) return false;
    seen.add(normalized);
    return true;
  });
}

// Phones: remove all non-digits
function normalizePhone(s: string): string {
  return s.replace(/[^0-9]/g, "");
}

// Emails: lowercase and trim (preserve @ and .)
function normalizeEmail(s: string): string {
  return s.toLowerCase().trim();
}

// Addresses: lowercase and remove extra whitespace/punctuation
function normalizeAddress(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function mergeContactInfo(patientInfo: ContactInfo, matchesInfo: ContactInfo): ContactInfo {
  return {
    phones: dedupe([...patientInfo.phones, ...matchesInfo.phones], normalizePhone),
    emails: dedupe([...patientInfo.emails, ...matchesInfo.emails], normalizeEmail),
    addresses: dedupe([...patientInfo.addresses, ...matchesInfo.addresses], normalizeAddress),
  };
}

function ContactSection({
  title,
  items,
  emptyMessage,
}: {
  title: string;
  items: string[];
  emptyMessage: string;
}) {
  return (
    <Box>
      <Heading size="sm" mb={2}>
        {title}
      </Heading>
      {items.length > 0 ? (
        <VStack align="start" gap={1} pl={2}>
          {items.map((item, index) => (
            <Text key={index} fontSize="md">
              • {item}
            </Text>
          ))}
        </VStack>
      ) : (
        <Text fontSize="md" color="gray.500" pl={2}>
          {emptyMessage}
        </Text>
      )}
    </Box>
  );
}

export function DemographicsModal({
  isOpen,
  patient,
  lastDocQuery,
  patientMatches = [],
  actions,
}: {
  isOpen: boolean;
  patient: PatientDTO | undefined;
  lastDocQuery: string | undefined;
  patientMatches?: PatientFacilityMatches;
  actions: {
    onCloseModal: () => void;
  };
}) {
  const contactInfo = useMemo(() => {
    const patientInfo = extractContactInfoFromPatient(patient);
    const matchesInfo = extractContactInfoFromMatches(patientMatches);
    return mergeContactInfo(patientInfo, matchesInfo);
  }, [patient, patientMatches]);

  const dividerColor = useColorModeValue("gray.300", "gray.600");
  const { phones, emails, addresses } = contactInfo;

  return (
    <Modal isOpen={isOpen} onClose={actions.onCloseModal} size="3xl">
      <ModalOverlay />
      <ModalContent m={5} p={6} borderWidth="1px" borderRadius="lg" shadow="md">
        <ModalCloseButton />
        <Box as="section" p={5}>
          <Text fontSize="2xl" fontWeight="bold" textAlign="center">
            {patient?.firstName} {patient?.lastName}
          </Text>

          <Text mt={2} fontSize="md" textAlign="center">
            {patient?.id}
          </Text>

          {lastDocQuery && (
            <Text mt={2} fontSize="md" textAlign="center" fontStyle="italic">
              Last Refreshed: {lastDocQuery}
            </Text>
          )}

          <Divider borderColor={dividerColor} my={4} />

          <Box display="flex" gap={4} mb={4}>
            <Box flex={1}>
              <Text fontWeight="semibold">Date of Birth:</Text>
              <Text fontSize="md">{patient?.dob}</Text>
            </Box>
            <Box flex={1}>
              <Text fontWeight="semibold">Gender at Birth:</Text>
              <Text fontSize="md">{patient?.genderAtBirth}</Text>
            </Box>
          </Box>

          <Divider borderColor={dividerColor} my={4} />

          <VStack gap={4} align="stretch">
            <ContactSection title="Phone" items={phones} emptyMessage="No phone on file" />
            <Divider borderColor={dividerColor} />
            <ContactSection title="Email" items={emails} emptyMessage="No email on file" />
            <Divider borderColor={dividerColor} />
            <ContactSection title="Address" items={addresses} emptyMessage="No address on file" />
          </VStack>
        </Box>
      </ModalContent>
    </Modal>
  );
}
